var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { useEffect, useRef } from "react";
import { TriggerType, } from "../../../types/forms";
import { evaluateValueExpression, handleGetRespectiveValue, verifyElementCondition, } from "../../../utils/elements-condition";
import { buildColumnTrackedSignature, cloneTriggerValue, resolveChoicesDisplayValue, } from "../../../utils/forms-functions";
export var useSurveyTriggers = function (_a) {
    var triggers = _a.triggers, formValues = _a.formValues, elementsTypeInfoByName = _a.elementsTypeInfoByName, setValue = _a.setValue, submitForm = _a.submitForm, isSubmitting = _a.isSubmitting, handleGoToElementByName = _a.handleGoToElementByName;
    var previousResultsRef = useRef(new Map());
    var previousSignaturesRef = useRef(new Map());
    var didInitializeRef = useRef(false);
    var triggersLengthRef = useRef(undefined);
    useEffect(function () {
        if (!(triggers === null || triggers === void 0 ? void 0 : triggers.length)) {
            return;
        }
        if (triggersLengthRef.current !== triggers.length) {
            triggersLengthRef.current = triggers.length;
            previousResultsRef.current.clear();
            previousSignaturesRef.current.clear();
            didInitializeRef.current = false;
        }
        triggers.forEach(function (trigger, index) {
            var _a;
            var _b, _c, _d;
            var result = trigger.expression
                ? verifyElementCondition(trigger.expression, formValues, elementsTypeInfoByName)
                : trigger.type === TriggerType.COPY_VALUE;
            var trackedExpression = trigger.type === TriggerType.RUN_EXPRESSION
                ? "".concat(trigger.expression, " ").concat(trigger.runExpression)
                : trigger.type === TriggerType.COPY_VALUE
                    ? "".concat((_b = trigger.expression) !== null && _b !== void 0 ? _b : "", " {").concat(trigger.fromName, "}")
                    : trigger.expression;
            var valuesForSignature = (trigger.type === TriggerType.SET_VALUE ||
                trigger.type === TriggerType.COPY_VALUE) &&
                !!trigger.setToName
                ? __assign(__assign({}, formValues), (_a = {}, _a[trigger.setToName] = null, _a)) : formValues;
            var signature = buildColumnTrackedSignature(trackedExpression, valuesForSignature);
            if (!didInitializeRef.current) {
                previousResultsRef.current.set(index, result);
                previousSignaturesRef.current.set(index, signature);
                return;
            }
            var previous = previousResultsRef.current.get(index);
            var previousSignature = previousSignaturesRef.current.get(index);
            previousResultsRef.current.set(index, result);
            previousSignaturesRef.current.set(index, signature);
            var conditionTurnedTrue = previous === false && result === true;
            var trackedValuesChanged = previousSignature !== signature;
            var isValueTrigger = trigger.type === TriggerType.RUN_EXPRESSION ||
                trigger.type === TriggerType.SET_VALUE ||
                trigger.type === TriggerType.COPY_VALUE;
            var shouldRun = isValueTrigger
                ? result && trackedValuesChanged
                : conditionTurnedTrue;
            if (!shouldRun) {
                return;
            }
            switch (trigger.type) {
                case TriggerType.SET_VALUE:
                    if (!trigger.setToName) {
                        break;
                    }
                    setValue(trigger.setToName, cloneTriggerValue(trigger.setValue), {
                        shouldValidate: false,
                        shouldDirty: true,
                    });
                    break;
                case TriggerType.COMPLETE:
                    if (!isSubmitting) {
                        submitForm();
                    }
                    break;
                case TriggerType.SKIP:
                    handleGoToElementByName(trigger.gotoName);
                    break;
                case TriggerType.RUN_EXPRESSION: {
                    if (!trigger.setToName || !trigger.runExpression) {
                        break;
                    }
                    var resolvedValue = evaluateValueExpression(trigger.runExpression, formValues);
                    if (resolvedValue === undefined) {
                        break;
                    }
                    setValue(trigger.setToName, resolvedValue, {
                        shouldValidate: false,
                        shouldDirty: true,
                    });
                    break;
                }
                case TriggerType.COPY_VALUE: {
                    if (!trigger.setToName || !trigger.fromName) {
                        break;
                    }
                    var copiedValue = (_c = handleGetRespectiveValue(trigger.fromName, formValues)) !== null && _c !== void 0 ? _c : undefined;
                    var valueToSet = trigger.copyDisplayValue
                        ? resolveChoicesDisplayValue(copiedValue, (_d = elementsTypeInfoByName[trigger.fromName]) === null || _d === void 0 ? void 0 : _d.choices)
                        : cloneTriggerValue(copiedValue);
                    setValue(trigger.setToName, valueToSet, {
                        shouldValidate: false,
                        shouldDirty: true,
                    });
                    break;
                }
                default:
                    break;
            }
        });
        if (!didInitializeRef.current) {
            didInitializeRef.current = true;
        }
    }, [
        triggers,
        formValues,
        elementsTypeInfoByName,
        setValue,
        submitForm,
        isSubmitting,
        handleGoToElementByName,
    ]);
};
